// src/contexts/socketContext.js
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';
import { useAuth } from './authContext';
import { playNotificationSound, setNotificationSoundsEnabled, initializeAudioContext } from '../utils/notificationSounds';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();
  const socketRef = useRef(null);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  // Early safety check to prevent null reference errors
  const safeUser = user || null;
  const safeIsAuthenticated = Boolean(isAuthenticated);
  const safeLoading = Boolean(loading);

  // Track user interaction to enable audio
  useEffect(() => {
    const handleUserInteraction = () => {
      if (!hasUserInteracted) {
        setHasUserInteracted(true);
        
        // Initialize audio context to unlock browser restrictions
        initializeAudioContext();
        
        // Enable notification sounds by default after first interaction
        const soundsEnabled = localStorage.getItem('notificationSoundsEnabled');
        
        if (soundsEnabled === null) {
          setNotificationSoundsEnabled(true);
        }
      }
    };

    // Listen for user interactions
    const events = ['click', 'touch', 'keydown', 'mousedown'];
    
    events.forEach(event => {
      document.addEventListener(event, handleUserInteraction, { once: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserInteraction);
      });
    };
  }, [hasUserInteracted]);

  useEffect(() => {
    const userId = safeUser?.id || safeUser?._id || null;
    
    // Don't attempt connection while still loading auth state
    if (safeLoading) {
      return;
    }

    // More robust check - ensure we have authentication, user object, and a valid user ID
    if (safeIsAuthenticated && safeUser && userId) {
      // Initialize socket connection
      const token = localStorage.getItem('token');
      
      if (!token) {
        return;
      }

      // Fix: Use base backend URL, not the API endpoint
      const backendUrl = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1".replace('/api/v1', '');
      
      socketRef.current = io(backendUrl, {
        auth: {
          token: token
        },
        transports: ['websocket'],
        timeout: 20000,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5,
      });

      const socket = socketRef.current;

      // Socket connection events
      socket.on('connect', () => {
        // Join user's personal room for notifications - use the correct event name
        socket.emit('joinRoom', userId);  // Backend expects 'joinRoom', not 'join'
      });

      // Handle successful room join
      socket.on('roomJoined', (data) => {
        // Room joined successfully
      });

      // Handle initial connection confirmation
      socket.on('connected', (data) => {
        // Connection confirmed by server
      });

      socket.on('disconnect', (reason) => {
        // Handle disconnection
      });

      socket.on('connect_error', (error) => {
        console.error('Socket connection error:', error);
      });

      socket.on('reconnect', (attemptNumber) => {
        // Reconnected successfully
      });

      socket.on('reconnect_attempt', (attemptNumber) => {
        // Attempting to reconnect
      });

      socket.on('reconnect_error', (error) => {
        console.error('Socket reconnection error:', error);
      });

      socket.on('reconnect_failed', () => {
        console.error('Socket reconnection failed');
      });

      // Handle incoming notifications
      socket.on('notification', (notificationData) => {
        // Play appropriate sound based on notification type (only if user has interacted)
        if (hasUserInteracted) {
          const soundType = mapNotificationTypeToSound(notificationData.type);
          playNotificationSound(soundType);
        }

        // Update unread count in localStorage and dispatch event
        const unreadCount = notificationData.unreadCount || 0;
        localStorage.setItem('unreadNotifications', unreadCount.toString());
        
        // Dispatch custom event to update UI components
        window.dispatchEvent(new CustomEvent('unreadCountUpdate', {
          detail: { unreadNotifications: unreadCount }
        }));
        
        // Also dispatch the notification data for other components
        window.dispatchEvent(new CustomEvent('newNotification', {
          detail: notificationData
        }));

        // Show browser notification if permission granted
        showBrowserNotification(notificationData);
      });

      // Handle incoming messages
      socket.on('message', (messageData) => {
        // Play chat sound for messages (only if user has interacted)
        if (hasUserInteracted) {
          playNotificationSound('chat');
        }

        // Update unread message count if provided
        const unreadCount = messageData.unreadCount || 0;
        localStorage.setItem('unreadMessages', unreadCount.toString());
        
        // Dispatch custom event to update UI components
        window.dispatchEvent(new CustomEvent('unreadMessagesUpdate', {
          detail: { unreadMessages: unreadCount }
        }));

        // Show browser notification for messages
        showBrowserNotification({
          title: `New message from ${messageData.senderName}`,
          message: messageData.message,
          type: 'message'
        });
      });

      // Handle unread count updates
      socket.on('unreadCountUpdate', (data) => {
        const unreadCount = data.unreadCount || 0;
        localStorage.setItem('unreadNotifications', unreadCount.toString());
        
        // Dispatch custom event to update UI components
        window.dispatchEvent(new CustomEvent('unreadCountUpdate', {
          detail: { unreadNotifications: unreadCount }
        }));
      });

      // Cleanup on unmount
      return () => {
        if (socket) {
          socket.disconnect();
        }
      };
    }
  }, [safeIsAuthenticated, safeUser, safeLoading, hasUserInteracted]);

  /**
   * Map notification types to appropriate sound types
   * @param {string} notificationType - Backend notification type
   * @returns {string} Sound type for audio utility
   */
  const mapNotificationTypeToSound = (notificationType) => {
    const soundMapping = {
      'team': 'team',
      'match': 'match',
      'event': 'event',
      'system': 'system',
      'alert': 'system',
      'achievement': 'team',
      'other': 'default'
    };
    
    return soundMapping[notificationType] || 'default';
  };

  /**
   * Show browser notification if permission granted
   * @param {Object} notificationData - Notification data
   */
  const showBrowserNotification = (notificationData) => {
    // Check if browser notifications are supported and permitted
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        const notification = new Notification(notificationData.title || 'Nashi Notification', {
          body: notificationData.message,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: notificationData.type,
          requireInteraction: false,
          silent: false
        });

        // Auto-close after 5 seconds
        setTimeout(() => {
          notification.close();
        }, 5000);

        // Handle notification click
        notification.onclick = () => {
          window.focus();
          notification.close();
        };

      } catch (error) {
        console.error('Error creating browser notification:', error);
      }
    }
  };

  /**
   * Request notification permission
   */
  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } else {
      return false;
    }
  };

  const value = {
    socket: socketRef.current,
    requestNotificationPermission,
    hasUserInteracted,
    // Test function for debugging
    simulateNotification: (type = 'default', message = 'Test notification', unreadCount = 1) => {
      const notificationData = {
        type,
        message,
        title: 'Test Notification',
        unreadCount,
        timestamp: new Date().toISOString()
      };
      
      // Trigger all the same events that would happen from a real socket notification
      if (hasUserInteracted) {
        const soundType = mapNotificationTypeToSound(type);
        playNotificationSound(soundType);
      }
      
      localStorage.setItem('unreadNotifications', unreadCount.toString());
      
      window.dispatchEvent(new CustomEvent('unreadCountUpdate', {
        detail: { unreadNotifications: unreadCount }
      }));
      
      window.dispatchEvent(new CustomEvent('newNotification', {
        detail: notificationData
      }));
    },
    
    // Test function for debugging messages
    simulateMessage: (message = 'Test message', senderName = 'Test User', unreadCount = 1) => {
      // Trigger all the same events that would happen from a real socket message
      if (hasUserInteracted) {
        playNotificationSound('chat');
      }
      
      localStorage.setItem('unreadMessages', unreadCount.toString());
      
      window.dispatchEvent(new CustomEvent('unreadMessagesUpdate', {
        detail: { unreadMessages: unreadCount }
      }));
    }
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
}; 